import React, { useState, useEffect, useCallback } from 'react';
import { domainService, Domain } from '../../services/domainService';

interface DomainAdminStats {
  total_domains: number;
  active_domains: number;
  pending_domains: number;
  suspended_domains: number;
  expired_status_domains: number;
  auto_renew_enabled: number;
  privacy_protected: number;
  expired_domains: number;   // expiration_date <= today (date-based, not status)
  expiring_soon: number;     // expiration_date within next 30 days
}

const DOMAINS_PER_PAGE = 10;

interface ServerPagination {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  limit: number;
  hasNext: boolean;
  hasPrev: boolean;
}

// P5: shape of a provisioning attempt the admin can act on
interface ProvisioningAttempt {
  id: string;
  order_id: string;
  domain_name: string;
  status: 'pending' | 'in_progress' | 'completed' | 'failed' | 'awaiting_admin' | 'refunded';
  aws_operation_id: string | null;
  aws_operation_status: string | null;
  attempts: number;
  last_error: string | null;
  pricing_paid: number | null;
  stripe_payment_intent_id: string | null;
  created_at: string;
  started_at: string | null;
  order_number: string | null;
  total_amount: string | null;
  payment_status: string | null;
  customer_email: string | null;
  customer_first_name: string | null;
  customer_last_name: string | null;
}

// P6: shape of the billing reconciliation response
interface ReconciliationRow {
  domain: string;
  tld: string;
  expiration_date: string | null;
  auto_renew: boolean;
  customer_charged: number;
  aws_registration_usd: number | null;
  aws_renewal_usd: number | null;
  margin: number | null;
  profitable: boolean | null;
}
interface ReconciliationSummary {
  domain_count: number;
  total_customer_charged_usd: number;
  total_aws_recurring_cost_usd: number;
  estimated_annual_margin_usd: number;
  unprofitable_domains: number;
  tlds_priced: number;
  tlds_missing_price: string[];
}

const DomainManagement: React.FC = () => {
  const [domains, setDomains] = useState<Domain[]>([]);
  const [pagination, setPagination] = useState<ServerPagination | null>(null);
  const [stats, setStats] = useState<DomainAdminStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [info, setInfo] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [syncing, setSyncing] = useState(false);
  // Per-domain in-flight action tracking so we can disable buttons during a
  // request and avoid the user double-clicking and firing duplicates.
  const [pendingAction, setPendingAction] = useState<{ id: string; action: string } | null>(null);

  // P5: provisioning attempts needing attention
  const [attempts, setAttempts] = useState<ProvisioningAttempt[]>([]);
  const [retryingAttemptId, setRetryingAttemptId] = useState<string | null>(null);

  // P6: billing reconciliation
  const [reconRows, setReconRows] = useState<ReconciliationRow[] | null>(null);
  const [reconSummary, setReconSummary] = useState<ReconciliationSummary | null>(null);
  const [reconExpanded, setReconExpanded] = useState(false);
  const [reconLoading, setReconLoading] = useState(false);

  const fetchDomains = useCallback(async (page: number = 1) => {
    try {
      const result = await domainService.getAllDomainsAdmin({ page, limit: DOMAINS_PER_PAGE });
      if (result.success) {
        setDomains(result.data.domains || []);
        setPagination(result.data.pagination || null);
      } else {
        setError(result.error || 'Failed to fetch domains');
      }
    } catch (err) {
      setError('Failed to fetch domains');
      console.error('Error fetching domains:', err);
    }
  }, []);

  const fetchStats = useCallback(async () => {
    try {
      const result = await domainService.getDomainStatsAdmin();
      if (result.success && result.data) {
        setStats(result.data as DomainAdminStats);
      }
    } catch (err) {
      console.error('Error fetching domain stats:', err);
    }
  }, []);

  // P5: fetch provisioning attempts that need admin attention (awaiting_admin + failed)
  const fetchAttempts = useCallback(async () => {
    try {
      const result = await domainService.listProvisioningAttemptsAdmin('needs_attention');
      if (result.success && result.data) {
        setAttempts(result.data.attempts || []);
      }
    } catch (err) {
      console.error('Error fetching provisioning attempts:', err);
    }
  }, []);

  // P6: fetch billing reconciliation. Slower (calls AWS ListPrices), so we
  // only load it lazily when the admin expands the panel.
  const fetchReconciliation = useCallback(async () => {
    try {
      setReconLoading(true);
      const result = await domainService.getDomainBillingReconciliationAdmin();
      if (result.success && result.data) {
        setReconRows(result.data.rows || []);
        setReconSummary(result.data.summary || null);
      } else {
        setError(result.error || 'Failed to fetch reconciliation data');
      }
    } catch (err) {
      console.error('Error fetching reconciliation:', err);
    } finally {
      setReconLoading(false);
    }
  }, []);

  const handleRetryAttempt = async (attemptId: string) => {
    if (!window.confirm('Retry this provisioning attempt? This will re-submit the order to AWS Route 53.')) {
      return;
    }
    try {
      setRetryingAttemptId(attemptId);
      setError('');
      setInfo('');
      const result = await domainService.retryProvisioningAdmin(attemptId);
      if (result.success) {
        setInfo(result.message || 'Retry started.');
        await Promise.all([fetchAttempts(), fetchDomains(currentPage), fetchStats()]);
      } else {
        setError(result.error || 'Failed to retry attempt');
      }
    } catch (err) {
      console.error('Retry error:', err);
      setError('Failed to retry attempt');
    } finally {
      setRetryingAttemptId(null);
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await Promise.all([fetchDomains(currentPage), fetchStats(), fetchAttempts()]);
      setLoading(false);
    };
    load();
  }, [fetchDomains, fetchStats, fetchAttempts, currentPage]);

  const handleSyncAwsDomains = async () => {
    try {
      setSyncing(true);
      setError('');
      setInfo('');
      const result = await domainService.syncAwsDomainsAdmin();
      if (result.success) {
        const synced = result.data?.synced ?? 0;
        const errors = result.data?.errors ?? 0;
        setInfo(`Synced ${synced} domain(s) from AWS${errors > 0 ? ` (${errors} error(s))` : ''}.`);
        await Promise.all([fetchDomains(currentPage), fetchStats()]);
      } else {
        setError(result.error || 'Failed to sync AWS domains');
      }
    } catch (err) {
      console.error('Error syncing AWS domains:', err);
      setError('Failed to sync AWS domains');
    } finally {
      setSyncing(false);
    }
  };

  const handleAdminUpdate = async (
    domainId: string,
    action: 'activate' | 'suspend',
    updateData: { status: 'active' | 'suspended' }
  ) => {
    if (action === 'suspend') {
      const domain = domains.find(d => d.id === domainId);
      const name = domain?.domain_name || 'this domain';
      if (!window.confirm(`Suspend ${name}? Customer will lose access in our records (note: this is currently a DB-only flag and does not change Route 53 status).`)) {
        return;
      }
    }
    try {
      setPendingAction({ id: domainId, action });
      setError('');
      setInfo('');
      const result = await domainService.updateDomainAdmin(domainId, updateData);
      if (result.success) {
        await Promise.all([fetchDomains(currentPage), fetchStats()]);
      } else {
        setError(result.error || `Failed to ${action} domain`);
      }
    } catch (err) {
      console.error(`Error performing ${action} on domain:`, err);
      setError(`Failed to ${action} domain`);
    } finally {
      setPendingAction(null);
    }
  };

  const handleRenew = async (domainId: string, domainName: string) => {
    const yearsStr = window.prompt(
      `Renew ${domainName} via AWS Route 53. How many years?`,
      '1'
    );
    if (yearsStr === null) return;
    const years = parseInt(yearsStr, 10);
    if (!Number.isFinite(years) || years < 1 || years > 10) {
      setError('Years must be a whole number between 1 and 10.');
      return;
    }
    if (!window.confirm(
      `Renew ${domainName} for ${years} year(s)?\n\n` +
      `This will call AWS Route 53 RenewDomain and charge your AWS account at the registry rate. ` +
      `The new expiry will be written to the database.`
    )) {
      return;
    }
    try {
      setPendingAction({ id: domainId, action: 'renew' });
      setError('');
      setInfo('');
      const result = await domainService.renewDomainAdmin(domainId, years);
      if (result.success) {
        setInfo(result.message || `Renewed ${domainName}.`);
        await Promise.all([fetchDomains(currentPage), fetchStats()]);
      } else {
        setError(result.error || 'Failed to renew domain');
      }
    } catch (err) {
      console.error('Error renewing domain:', err);
      setError('Failed to renew domain');
    } finally {
      setPendingAction(null);
    }
  };

  const handleDelete = async (domainId: string, domainName: string) => {
    if (!window.confirm(
      `Delete "${domainName}" from our database?\n\n` +
      `IMPORTANT: this does NOT cancel the AWS Route 53 registration. ` +
      `AWS Route 53 has no delete-domain API; domains expire or are transferred out. ` +
      `Removing the row here means our admin and customer dashboards will stop showing it, ` +
      `but the domain will continue to renew at AWS unless you disable auto-renew first.`
    )) {
      return;
    }
    try {
      setPendingAction({ id: domainId, action: 'delete' });
      setError('');
      setInfo('');
      const result = await domainService.deleteDomainAdmin(domainId);
      if (result.success) {
        setInfo(result.message || `Removed ${domainName} from database.`);
        await Promise.all([fetchDomains(currentPage), fetchStats()]);
      } else {
        setError(result.error || 'Failed to delete domain');
      }
    } catch (err) {
      console.error('Error deleting domain:', err);
      setError('Failed to delete domain');
    } finally {
      setPendingAction(null);
    }
  };

  // Server returns one page at a time now (DOMAINS_PER_PAGE rows). Search
  // and status filter still apply client-side within the current page only;
  // for cross-page search we'd need to push search/status to the backend too.
  const visibleDomains = domains.filter(domain => {
    const matchesSearch = domain.domain_name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = selectedStatus === '' || domain.status === selectedStatus;
    return matchesSearch && matchesStatus;
  });

  const serverTotalPages = pagination?.totalPages ?? 1;
  const serverTotalCount = pagination?.totalCount ?? domains.length;
  const firstRowOnPage = ((currentPage - 1) * DOMAINS_PER_PAGE) + 1;
  const lastRowOnPage = Math.min(currentPage * DOMAINS_PER_PAGE, serverTotalCount);

  // The backend stores expiration_date/registration_date as Postgres DATE
  // (calendar date, no timezone). node-postgres serializes that as
  // "YYYY-MM-DDT00:00:00.000Z" — UTC midnight. If we feed that directly
  // into `new Date(...).toLocaleDateString()`, browsers west of UTC shift
  // it back to the previous day. Parse the YYYY-MM-DD prefix and build a
  // Date in *local* time so the displayed date matches the DB date.
  const parseCalendarDate = (raw: string | Date | null | undefined): Date | null => {
    if (!raw) return null;
    const s = typeof raw === 'string' ? raw : raw.toISOString();
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (!m) return null;
    const [, y, mo, d] = m;
    return new Date(parseInt(y), parseInt(mo) - 1, parseInt(d));
  };

  const formatDate = (dateString: string) => {
    const d = parseCalendarDate(dateString);
    if (!d) return '—';
    return d.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800';
      case 'suspended':
        return 'bg-red-100 text-red-800';
      case 'expired':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getDaysUntilExpiry = (expiryDate: string) => {
    const expiry = parseCalendarDate(expiryDate);
    if (!expiry) return 0;
    // Compare *calendar days* in local time, not millisecond differences,
    // so an expiry of today reads as 0 (not -1) regardless of clock time.
    const today = new Date();
    const startOfToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const startOfExpiry = new Date(expiry.getFullYear(), expiry.getMonth(), expiry.getDate());
    return Math.round((startOfExpiry.getTime() - startOfToday.getTime()) / (1000 * 60 * 60 * 24));
  };

  const getOwnerLabel = (d: Domain) => {
    if (d.company_name) return d.company_name;
    const fullName = `${d.first_name || ''} ${d.last_name || ''}`.trim();
    if (fullName) return fullName;
    return d.customer_email || 'Unassigned';
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="space-y-3">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // Stat numbers prefer the server-computed stats; fall back to deriving
  // from the in-memory list while stats are loading or if the endpoint fails.
  const totalCount = stats?.total_domains ?? domains.length;
  const activeCount = stats?.active_domains ?? domains.filter(d => d.status === 'active').length;
  const expiringSoonCount =
    stats?.expiring_soon ??
    domains.filter(d => {
      const days = getDaysUntilExpiry(d.expiration_date);
      return days > 0 && days <= 30;
    }).length;
  const expiredCount =
    stats?.expired_domains ??
    domains.filter(d => getDaysUntilExpiry(d.expiration_date) <= 0).length;
  const autoRenewCount = stats?.auto_renew_enabled ?? domains.filter(d => d.auto_renew).length;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Domain Management</h2>
          <p className="text-gray-600">Manage all customer domains</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={handleSyncAwsDomains}
            disabled={syncing}
            className="px-4 py-2 border border-orange-300 text-orange-700 rounded-lg hover:bg-orange-50 transition-colors duration-200 disabled:opacity-50"
          >
            {syncing ? 'Syncing...' : 'Sync AWS Domains'}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}
      {info && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-700">{info}</p>
        </div>
      )}

      {/* P5: Provisioning attempts needing admin attention. Only renders if
          there's at least one row, so it stays invisible in the happy path. */}
      {attempts.length > 0 && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg overflow-hidden">
          <div className="px-6 py-4 bg-amber-100 border-b border-amber-300 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <svg className="w-5 h-5 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M4.93 19.07A10 10 0 1119.07 4.93 10 10 0 014.93 19.07z" />
              </svg>
              <h3 className="font-semibold text-amber-900">
                Provisioning Needs Attention ({attempts.length})
              </h3>
            </div>
            <span className="text-xs text-amber-800">
              These customers paid but the domain didn't auto-register. Charge is held.
            </span>
          </div>
          <div className="divide-y divide-amber-200">
            {attempts.map((a) => (
              <div key={a.id} className="p-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-start">
                <div className="md:col-span-3">
                  <div className="font-mono text-sm font-semibold text-gray-900">{a.domain_name}</div>
                  <div className="text-xs text-gray-600 mt-1">
                    {a.customer_email || 'no email'}
                    {a.customer_first_name && (
                      <span> · {a.customer_first_name} {a.customer_last_name || ''}</span>
                    )}
                  </div>
                </div>
                <div className="md:col-span-2 text-xs">
                  <div className="text-gray-600">Order</div>
                  <div className="font-mono">{a.order_number || a.order_id.slice(0, 8)}</div>
                  {a.total_amount && (
                    <div className="text-gray-700 mt-1">${parseFloat(a.total_amount).toFixed(2)} {a.payment_status === 'paid' ? '· paid' : `· ${a.payment_status}`}</div>
                  )}
                </div>
                <div className="md:col-span-2 text-xs">
                  <div className="text-gray-600">Status</div>
                  <div className="font-medium text-amber-900 capitalize">{a.status.replace('_', ' ')}</div>
                  {a.aws_operation_status && (
                    <div className="text-gray-700 mt-1">AWS: <span className="font-mono">{a.aws_operation_status}</span></div>
                  )}
                  <div className="text-gray-500 mt-1">{a.attempts} attempt{a.attempts === 1 ? '' : 's'}</div>
                </div>
                <div className="md:col-span-3 text-xs text-gray-800 break-words">
                  {a.last_error ? (
                    <>
                      <div className="text-gray-600">Last error</div>
                      <div className="mt-1">{a.last_error}</div>
                    </>
                  ) : (
                    <div className="text-gray-500">No error message recorded.</div>
                  )}
                </div>
                <div className="md:col-span-2 flex flex-col gap-2">
                  <button
                    onClick={() => handleRetryAttempt(a.id)}
                    disabled={retryingAttemptId === a.id}
                    className="bg-amber-600 text-white px-3 py-1 rounded text-xs font-semibold hover:bg-amber-700 disabled:opacity-50"
                  >
                    {retryingAttemptId === a.id ? 'Retrying…' : 'Retry'}
                  </button>
                  {a.stripe_payment_intent_id && (
                    <a
                      href={`https://dashboard.stripe.com/payments/${a.stripe_payment_intent_id}`}
                      target="_blank"
                      rel="noreferrer noopener"
                      className="text-center border border-amber-400 text-amber-800 px-3 py-1 rounded text-xs font-semibold hover:bg-amber-100"
                    >
                      View in Stripe ↗
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-6 border-b border-gray-200">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Search domains..."
                value={searchTerm}
                onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent"
              />
            </div>
            <div className="w-full md:w-48">
              <select
                value={selectedStatus}
                onChange={(e) => { setSelectedStatus(e.target.value); setCurrentPage(1); }}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-navy focus:border-transparent"
              >
                <option value="">All Status</option>
                <option value="active">Active</option>
                <option value="pending">Pending</option>
                <option value="suspended">Suspended</option>
                <option value="expired">Expired</option>
              </select>
            </div>
          </div>
        </div>

        {visibleDomains.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
              </svg>
              <h3 className="text-lg font-medium text-gray-900 mb-2">No domains found</h3>
              <p className="text-gray-600 mb-4">
                {domains.length === 0
                  ? 'No domains are currently synced from AWS. Click "Sync AWS Domains" to import your domains.'
                  : 'No domains match your current filters.'}
              </p>
              {domains.length === 0 && (
                <button
                  onClick={handleSyncAwsDomains}
                  disabled={syncing}
                  className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors duration-200 disabled:opacity-50"
                >
                  {syncing ? 'Syncing...' : 'Sync AWS Domains Now'}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Domain</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Registration</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Expiry</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Auto Renew</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {visibleDomains.map((domain) => {
                  const daysUntilExpiry = getDaysUntilExpiry(domain.expiration_date);
                  const isExpiringSoon = daysUntilExpiry <= 30 && daysUntilExpiry > 0;
                  const isExpired = daysUntilExpiry <= 0;
                  const isPending = pendingAction?.id === domain.id;

                  return (
                    <tr key={domain.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="h-10 w-10 rounded-full bg-primary-navy flex items-center justify-center">
                            <span className="text-sm font-medium text-white">
                              {domain.domain_name.charAt(0).toUpperCase()}
                            </span>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {domain.domain_name}
                            </div>
                            <div className="text-sm text-gray-500">
                              {getOwnerLabel(domain)}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(domain.status)}`}>
                          {domain.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDate(domain.registration_date)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          {formatDate(domain.expiration_date)}
                        </div>
                        <div className={`text-xs ${isExpired ? 'text-red-600' : isExpiringSoon ? 'text-yellow-600' : 'text-gray-500'}`}>
                          {isExpired ? 'Expired' : isExpiringSoon ? `${daysUntilExpiry} days left` : `${daysUntilExpiry} days`}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                          domain.auto_renew ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {domain.auto_renew ? 'Enabled' : 'Disabled'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          {domain.status === 'suspended' ? (
                            <button
                              onClick={() => handleAdminUpdate(domain.id, 'activate', { status: 'active' })}
                              disabled={isPending}
                              className="text-green-600 hover:text-green-900 text-sm disabled:opacity-50"
                            >
                              {isPending && pendingAction?.action === 'activate' ? 'Activating…' : 'Activate'}
                            </button>
                          ) : (
                            <button
                              onClick={() => handleAdminUpdate(domain.id, 'suspend', { status: 'suspended' })}
                              disabled={isPending}
                              className="text-yellow-600 hover:text-yellow-900 text-sm disabled:opacity-50"
                            >
                              {isPending && pendingAction?.action === 'suspend' ? 'Suspending…' : 'Suspend'}
                            </button>
                          )}
                          {(isExpired || isExpiringSoon) && (
                            <button
                              onClick={() => handleRenew(domain.id, domain.domain_name)}
                              disabled={isPending}
                              className="text-blue-600 hover:text-blue-900 text-sm disabled:opacity-50"
                            >
                              {isPending && pendingAction?.action === 'renew' ? 'Renewing…' : 'Renew'}
                            </button>
                          )}
                          <button
                            onClick={() => handleDelete(domain.id, domain.domain_name)}
                            disabled={isPending}
                            className="text-red-600 hover:text-red-900 text-sm disabled:opacity-50"
                          >
                            {isPending && pendingAction?.action === 'delete' ? 'Deleting…' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {serverTotalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {firstRowOnPage} to {lastRowOnPage} of {serverTotalCount} results
                {(searchTerm || selectedStatus) && (
                  <span className="ml-2 text-xs text-gray-500">(search/filter applies to this page only)</span>
                )}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  disabled={!pagination?.hasPrev || currentPage === 1}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="px-3 py-1 text-sm bg-primary-navy text-white rounded">
                  {currentPage} / {serverTotalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(Math.min(serverTotalPages, currentPage + 1))}
                  disabled={!pagination?.hasNext || currentPage >= serverTotalPages}
                  className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-indigo-100 rounded-lg">
              <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Domains</p>
              <p className="text-2xl font-bold text-gray-900">{totalCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Domains</p>
              <p className="text-2xl font-bold text-gray-900">{activeCount}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.268 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">
                {expiredCount > 0 ? `Expiring / Expired` : 'Expiring Soon'}
              </p>
              <p className="text-2xl font-bold text-gray-900">
                {expiringSoonCount + expiredCount}
              </p>
              {expiredCount > 0 && (
                <p className="text-xs text-red-600 mt-1">{expiredCount} already expired</p>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Auto Renew</p>
              <p className="text-2xl font-bold text-gray-900">{autoRenewCount}</p>
            </div>
          </div>
        </div>
      </div>

      {/* P6: Billing reconciliation panel — collapsed by default. Expanding
          calls /admin/billing-reconciliation which goes to AWS ListPrices,
          so we don't want to fetch on every page load. */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Billing & Margin Reconciliation</h3>
            <p className="text-xs text-gray-500 mt-1">Compares what we charge customers against AWS Route 53's prices to surface domains where we're losing money.</p>
          </div>
          <button
            onClick={() => {
              if (!reconExpanded && !reconRows) {
                fetchReconciliation();
              }
              setReconExpanded(!reconExpanded);
            }}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-semibold disabled:opacity-50"
            disabled={reconLoading}
          >
            {reconLoading ? 'Loading…' : reconExpanded ? 'Hide' : 'Show Reconciliation'}
          </button>
        </div>

        {reconExpanded && (
          <div className="p-6">
            {reconLoading && !reconRows && (
              <div className="text-center py-6 text-gray-500 text-sm">Fetching AWS prices and computing margins…</div>
            )}

            {reconSummary && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
                <div className="bg-gray-50 rounded p-3">
                  <div className="text-xs text-gray-600">Domains</div>
                  <div className="text-xl font-bold text-gray-900">{reconSummary.domain_count}</div>
                </div>
                <div className="bg-green-50 rounded p-3">
                  <div className="text-xs text-gray-600">Total Charged</div>
                  <div className="text-xl font-bold text-green-700">${reconSummary.total_customer_charged_usd.toFixed(2)}</div>
                </div>
                <div className="bg-orange-50 rounded p-3">
                  <div className="text-xs text-gray-600">AWS Annual Cost</div>
                  <div className="text-xl font-bold text-orange-700">${reconSummary.total_aws_recurring_cost_usd.toFixed(2)}</div>
                </div>
                <div className={`rounded p-3 ${reconSummary.estimated_annual_margin_usd >= 0 ? 'bg-emerald-50' : 'bg-red-50'}`}>
                  <div className="text-xs text-gray-600">Annual Margin</div>
                  <div className={`text-xl font-bold ${reconSummary.estimated_annual_margin_usd >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                    {reconSummary.estimated_annual_margin_usd >= 0 ? '+' : ''}${reconSummary.estimated_annual_margin_usd.toFixed(2)}
                  </div>
                </div>
                <div className={`rounded p-3 ${reconSummary.unprofitable_domains > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                  <div className="text-xs text-gray-600">Unprofitable</div>
                  <div className={`text-xl font-bold ${reconSummary.unprofitable_domains > 0 ? 'text-red-700' : 'text-gray-900'}`}>
                    {reconSummary.unprofitable_domains}
                  </div>
                </div>
              </div>
            )}

            {reconSummary && reconSummary.tlds_missing_price.length > 0 && (
              <div className="mb-4 bg-yellow-50 border border-yellow-200 rounded p-3 text-xs text-yellow-900">
                Could not retrieve AWS prices for: <span className="font-mono">{reconSummary.tlds_missing_price.join(', ')}</span>. These domains are excluded from cost/margin numbers.
              </div>
            )}

            {reconRows && reconRows.length > 0 && (
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-600 uppercase">Domain</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase">Charged</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase">AWS Reg</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase">AWS Renew</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-600 uppercase">Margin</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {reconRows.map((r) => (
                      <tr key={r.domain} className={r.profitable === false ? 'bg-red-50' : ''}>
                        <td className="px-3 py-2 font-mono text-gray-900">{r.domain}</td>
                        <td className="px-3 py-2 text-right text-gray-900">${r.customer_charged.toFixed(2)}</td>
                        <td className="px-3 py-2 text-right text-gray-700">{r.aws_registration_usd !== null ? `$${r.aws_registration_usd.toFixed(2)}` : '—'}</td>
                        <td className="px-3 py-2 text-right text-gray-700">{r.aws_renewal_usd !== null ? `$${r.aws_renewal_usd.toFixed(2)}` : '—'}</td>
                        <td className={`px-3 py-2 text-right font-semibold ${r.margin === null ? 'text-gray-500' : r.margin >= 0 ? 'text-emerald-700' : 'text-red-700'}`}>
                          {r.margin === null ? '—' : `${r.margin >= 0 ? '+' : ''}$${r.margin.toFixed(2)}`}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DomainManagement;
